import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useCloseOnRouteChange } from '../../../Hooks/useCloseOnRouteChange';
import { useSignOut } from '../../../Hooks/useSignOut';
import { useTempEdgeTranslator } from '../../../Hooks/useTranslation';
import { toTestId } from '../../../utils/helpers/toTestId';
import CollapsibleGroup from './CollapsibleGroup';
import MenuColumn from './MenuColumn';
import MenuItemLink from './MenuItemLink';
import MobileDrawer from './MobileDrawer';
import { useNavTabs, useSettingsItems } from './NavMenu';
import SignOutButton from './SignOutButton';
import STATIC_NAV_LINKS from './staticNavLinks';
import './MobileMenu.css';

const SETTINGS_LABEL = 'com.tempedge.msg.menu.settings';

const MobileMenu = ({ show, onClose }) => {
  const { pathname } = useLocation();
  const { tempedgeTranslator, locale: lang } = useTempEdgeTranslator();
  const tabs = useNavTabs();
  const settingsItems = useSettingsItems();
  const [openTab, setOpenTab] = useState(null);

  useCloseOnRouteChange(onClose);

  const handleSignOut = useSignOut(onClose);

  const renderTabLink = (labelKey, path) => {
    const targetPath = `${path}/${lang}`;
    const isActive = pathname === targetPath;
    const testId = `mobile-menu-link-${toTestId(path)}`;
    return (
      <li key={path} className="te-mobile-menu-group">
        <Link
          className={`te-mobile-menu-group-trigger te-focus-ring-inset ${isActive ? 'active' : ''}`}
          to={targetPath}
          onClick={onClose}
          data-testid={testId}
        >
          {tempedgeTranslator(labelKey)}
        </Link>
      </li>
    );
  };

  return (
    <MobileDrawer show={show} onClose={onClose}>
      <ul className="te-mobile-menu-groups" data-testid="mobile-menu-groups">
        {STATIC_NAV_LINKS.map((item) => renderTabLink(item.labelKey, item.path))}

        {tabs.map((tab) => {
          if (tab.columns.length === 0) return renderTabLink(tab.category, tab.optionPath);

          const isOpen = openTab === tab.category;
          const testId = `mobile-menu-group-${toTestId(tab.category)}`;

          return (
            <CollapsibleGroup
              key={tab.category}
              label={tempedgeTranslator(tab.category)}
              isOpen={isOpen}
              onToggle={() => setOpenTab((prev) => (prev === tab.category ? null : tab.category))}
              testId={testId}
            >
              {tab.columns.map((column) => (
                <MenuColumn
                  key={column.key}
                  column={column}
                  lang={lang}
                  colClassName="te-mobile-menu-col"
                  labelClassName="te-mobile-menu-col-label"
                  iconClassName="te-menu-item-icon te-mobile-menu-item-icon"
                  linkClassName="te-focus-ring-inset"
                  onItemClick={onClose}
                />
              ))}
            </CollapsibleGroup>
          );
        })}

        {settingsItems.length > 0 && (
          <CollapsibleGroup
            label={tempedgeTranslator(SETTINGS_LABEL)}
            isOpen={openTab === SETTINGS_LABEL}
            onToggle={() => setOpenTab((prev) => (prev === SETTINGS_LABEL ? null : SETTINGS_LABEL))}
            testId="mobile-menu-group-settings"
          >
            <ul>
              {settingsItems.map((item) => (
                <MenuItemLink
                  key={item.optionPath}
                  item={item}
                  lang={lang}
                  iconClassName="te-menu-item-icon te-mobile-menu-item-icon"
                  linkClassName="te-focus-ring-inset"
                  onClick={onClose}
                />
              ))}
            </ul>
          </CollapsibleGroup>
        )}
      </ul>

      <SignOutButton className="te-mobile-menu-signout te-focus-ring-inset" onClick={handleSignOut} testId="mobile-menu-signout" />
    </MobileDrawer>
  );
};

export default MobileMenu;
